/**
 * Live Lalamove delivery options for a credential set: vehicle classes and
 * the special requests valid for each (per city). Backs the dynamic
 * service_type / special_requests fields in the Integrations drawer edit mode.
 */
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'

export interface SpecialRequestOption {
  name: string
  description: string
  parent_type: string
  max_selection: number | null
}

export interface ServiceOptions {
  key: string
  description: string
  load_value: string
  load_unit: string
  special_requests: SpecialRequestOption[]
}

export interface DeliveryOptions {
  cities: { locode: string; name: string; services: ServiceOptions[] }[]
}

export function useDeliveryOptions(setId: string, enabled: boolean) {
  return useQuery<DeliveryOptions>({
    queryKey: ['lalamove-delivery-options', setId],
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.fetch(
        `/api/admin/credential-sets/${setId}/entries/lalamove/delivery-options`,
      )
      if (!res.ok) {
        // Surface the backend's reason so the drawer's failure notice can show
        // it — a bare "failed" leaves the admin unable to act on the error.
        let detail = `HTTP ${res.status}`
        try {
          const body = await res.json()
          if (typeof body?.detail === 'string' && body.detail) detail = body.detail
        } catch { /* non-JSON error body */ }
        throw new Error(detail)
      }
      return res.json()
    },
  })
}
